// tape S03: the winamp-style "tape deck" — a StageWidget that renders the
// control face and binds to the persistent engine. Closing the widget does NOT
// stop playback (the engine lives on); re-opening reflects live state.

import type { StageWidget, StageHandle, StageCtx } from '../stage/types'
import { TRACKS, ALBUM, fmtTime, totalRuntime } from './tracks'
import {
  getTapeState, onTapeChange, getAnalyser,
  tapePlay, tapeToggle, tapeStop, tapeNext, tapePrev, tapeSeek, tapeSetVolume,
} from './engine'

function reducedMotion(): boolean {
  return document.body.classList.contains('vt-reduced-motion')
}

function btn(icon: string, label: string): HTMLButtonElement {
  const b = document.createElement('button')
  b.className = `vt-tape-btn vt-tape-btn-${icon}`
  b.setAttribute('aria-label', label)
  b.innerHTML = '<span class="vt-tape-glyph" aria-hidden="true"></span>'
  return b
}

export const tapeWidget: StageWidget = {
  id: 'tape',
  label: 'TAPE DECK // PROBABLY — VAGUEPOST KING',

  mount(host: HTMLElement, _ctx: StageCtx): StageHandle {
    host.classList.add('vt-tape-host')

    const root = document.createElement('div')
    root.className = 'vt-tape'

    // ---- deck: cover + display -------------------------------------------
    const deck = document.createElement('div')
    deck.className = 'vt-tape-deck'

    const cover = document.createElement('img')
    cover.className = 'vt-tape-cover'
    cover.src = ALBUM.cover
    cover.alt = 'probably — cover'
    cover.decoding = 'async'
    deck.appendChild(cover)

    const display = document.createElement('div')
    display.className = 'vt-tape-display'

    const readout = document.createElement('div')
    readout.className = 'vt-tape-readout'
    const timeEl = document.createElement('span')
    timeEl.className = 'vt-tape-time'
    timeEl.textContent = '0:00'
    const durEl = document.createElement('span')
    durEl.className = 'vt-tape-dur'
    durEl.textContent = '/ --:--'
    const flav = document.createElement('span')
    flav.className = 'vt-tape-flav'
    flav.textContent = '▮ V0·stereo'
    readout.append(timeEl, durEl, flav)

    const marquee = document.createElement('div')
    marquee.className = 'vt-tape-marquee'
    const marqueeInner = document.createElement('span')
    marqueeInner.className = 'vt-tape-marquee-inner'
    marquee.appendChild(marqueeInner)

    const viz = document.createElement('canvas')
    viz.className = 'vt-tape-viz'

    display.append(readout, marquee, viz)
    deck.append(display)
    root.appendChild(deck)

    // ---- seek bar ---------------------------------------------------------
    const seek = document.createElement('div')
    seek.className = 'vt-tape-seek'
    seek.setAttribute('role', 'slider')
    seek.setAttribute('aria-label', 'seek')
    const seekFill = document.createElement('div')
    seekFill.className = 'vt-tape-seek-fill'
    const seekThumb = document.createElement('div')
    seekThumb.className = 'vt-tape-seek-thumb'
    seek.append(seekFill, seekThumb)
    root.appendChild(seek)

    // ---- transport --------------------------------------------------------
    const transport = document.createElement('div')
    transport.className = 'vt-tape-transport'

    const prevB = btn('prev', 'previous track')
    const playB = btn('play', 'play / pause')
    const stopB = btn('stop', 'stop')
    const nextB = btn('next', 'next track')

    const volWrap = document.createElement('div')
    volWrap.className = 'vt-tape-vol'
    const volLabel = document.createElement('span')
    volLabel.className = 'vt-tape-vol-label'
    volLabel.textContent = 'vol'
    const vol = document.createElement('input')
    vol.type = 'range'
    vol.min = '0'; vol.max = '100'; vol.step = '1'
    vol.className = 'vt-tape-vol-input'
    vol.setAttribute('aria-label', 'volume')
    volWrap.append(volLabel, vol)

    transport.append(prevB, playB, stopB, nextB, volWrap)
    root.appendChild(transport)

    // ---- tracklist --------------------------------------------------------
    const caption = document.createElement('div')
    caption.className = 'vt-tape-caption'
    caption.textContent = `▮ ${TRACKS.length} tracks · ${fmtTime(totalRuntime())} · self-hosted`
    root.appendChild(caption)

    const list = document.createElement('div')
    list.className = 'vt-tape-list'
    const rows: HTMLElement[] = []
    for (const t of TRACKS) {
      const row = document.createElement('button')
      row.className = 'vt-tape-row'
      row.dataset.i = String(t.n - 1)
      const num = document.createElement('span')
      num.className = 'vt-tape-row-n'
      num.textContent = String(t.n).padStart(2, '0')
      const title = document.createElement('span')
      title.className = 'vt-tape-row-t'
      title.textContent = t.title
      const dur = document.createElement('span')
      dur.className = 'vt-tape-row-d'
      dur.textContent = fmtTime(t.duration)
      row.append(num, title, dur)
      row.addEventListener('click', () => tapePlay(t.n - 1))
      list.appendChild(row)
      rows.push(row)
    }
    root.appendChild(list)

    // ---- links ------------------------------------------------------------
    const links = document.createElement('div')
    links.className = 'vt-tape-links'
    const sc = document.createElement('a')
    sc.className = 'vt-tape-link'
    sc.href = ALBUM.soundcloud
    sc.target = '_blank'; sc.rel = 'noopener noreferrer'
    sc.textContent = '[ soundcloud ↗ ]'
    const ps = document.createElement('a')
    ps.className = 'vt-tape-link'
    ps.href = ALBUM.presave
    ps.target = '_blank'; ps.rel = 'noopener noreferrer'
    ps.textContent = '[ presave ↗ ]'
    const dl = document.createElement('a')
    dl.className = 'vt-tape-link'
    dl.textContent = '[ download ]'
    links.append(sc, ps, dl)
    root.appendChild(links)

    host.appendChild(root)

    // ---- wiring -----------------------------------------------------------
    playB.addEventListener('click', () => tapeToggle())
    stopB.addEventListener('click', () => tapeStop())
    nextB.addEventListener('click', () => tapeNext())
    prevB.addEventListener('click', () => tapePrev())
    vol.addEventListener('input', () => tapeSetVolume(Number(vol.value) / 100))

    // seek (click + drag)
    let seeking = false
    const durOf = () => {
      const s = getTapeState()
      return s.duration || TRACKS[s.index].duration
    }
    const seekFromEvent = (clientX: number) => {
      const r = seek.getBoundingClientRect()
      const frac = Math.min(Math.max((clientX - r.left) / r.width, 0), 1)
      tapeSeek(frac * durOf())
    }
    seek.addEventListener('pointerdown', (e) => {
      seeking = true
      seek.setPointerCapture(e.pointerId)
      seekFromEvent(e.clientX)
    })
    seek.addEventListener('pointermove', (e) => { if (seeking) seekFromEvent(e.clientX) })
    seek.addEventListener('pointerup', (e) => { seeking = false; seek.releasePointerCapture(e.pointerId) })
    seek.addEventListener('pointercancel', () => { seeking = false })

    // ---- render bind ------------------------------------------------------
    let lastTitleI = -1
    const render = (s = getTapeState()) => {
      timeEl.textContent = fmtTime(s.currentTime)
      durEl.textContent = `/ ${fmtTime(s.duration)}`
      const frac = s.duration > 0 ? s.currentTime / s.duration : 0
      if (!seeking) {
        seekFill.style.width = `${frac * 100}%`
        seekThumb.style.left = `${frac * 100}%`
      }
      playB.classList.toggle('is-playing', s.playing)
      root.classList.toggle('is-playing', s.playing)
      vol.value = String(Math.round(s.volume * 100))

      rows.forEach((r, i) => {
        r.classList.toggle('is-current', i === s.index)
        r.classList.toggle('is-playing', i === s.index && s.playing)
      })

      if (s.index !== lastTitleI) {
        lastTitleI = s.index
        const t = TRACKS[s.index]
        marqueeInner.textContent = `${String(t.n).padStart(2, '0')} · ${t.title} · vaguepost king · probably`
        dl.setAttribute('href', t.src)
        dl.setAttribute('download', `${String(t.n).padStart(2, '0')} ${t.title}.mp3`)
      }
    }

    const unsub = onTapeChange(render)
    render()

    // ---- visualizer -------------------------------------------------------
    let rafId = 0
    const ctx2d = viz.getContext('2d')
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const sizeCanvas = () => {
      const w = viz.clientWidth || 240
      const h = viz.clientHeight || 38
      viz.width = Math.round(w * dpr)
      viz.height = Math.round(h * dpr)
    }
    const ro = new ResizeObserver(sizeCanvas)
    ro.observe(viz)
    sizeCanvas()

    const drawStatic = () => {
      if (!ctx2d) return
      const w = viz.width, h = viz.height
      ctx2d.clearRect(0, 0, w, h)
      ctx2d.fillStyle = 'rgba(0,90,0,0.55)'
      const bars = 24, gap = 2 * dpr
      const bw = (w - gap * (bars - 1)) / bars
      for (let i = 0; i < bars; i++) {
        const bh = Math.max(2 * dpr, h * (0.10 + 0.06 * Math.sin(i * 0.9)))
        ctx2d.fillRect(i * (bw + gap), h - bh, bw, bh)
      }
    }

    const analyser = getAnalyser()
    if (analyser && !reducedMotion()) {
      const bins = analyser.frequencyBinCount
      const data = new Uint8Array(bins)
      const bars = Math.min(24, bins - 2)
      const loop = () => {
        rafId = requestAnimationFrame(loop)
        if (!ctx2d) return
        const playing = getTapeState().playing
        const w = viz.width, h = viz.height
        ctx2d.clearRect(0, 0, w, h)
        const gap = 2 * dpr
        const bw = (w - gap * (bars - 1)) / bars
        if (playing) analyser.getByteFrequencyData(data)
        for (let i = 0; i < bars; i++) {
          const v = playing ? data[i + 1] / 255 : 0.04 + 0.03 * Math.sin(i)
          const bh = Math.max(2 * dpr, v * h)
          const x = i * (bw + gap)
          ctx2d.fillStyle = 'rgba(0,210,0,0.85)'
          ctx2d.fillRect(x, h - bh, bw, bh)
          // bright cap
          ctx2d.fillStyle = 'rgba(255,176,0,0.9)'
          ctx2d.fillRect(x, h - bh, bw, Math.min(2 * dpr, bh))
        }
      }
      loop()
    } else {
      drawStatic()
    }

    return {
      dismiss() {
        if (rafId) cancelAnimationFrame(rafId)
        ro.disconnect()
        unsub()
        host.classList.remove('vt-tape-host')
      },
    }
  },
}
